import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import IconTrash2 from "@/icons/IconTrash2";
import IconEditPencil2 from "@/icons/IconEditPencil2";
import IconCheckMark from "@/icons/IconCheckMark";
import { IconClose } from "@velaro/velaro-component-lib";
import { AlertTriangle } from "@velaro/velaro-component-lib";
import React, { useEffect, useRef, useState } from "react";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { PrimaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib/";
import { useNavigate } from "react-router-dom";

interface ShopifyConfig {
  id: number;
  storeName: string;
  appClientId: string;
  appSecret: string;
  domain: string;
  teamId: number;
}

interface Props {
  configs: ShopifyConfig[];
  deleteConfig(id: number): void;
  addConfig(): void;
  teamId: number;
  onChange(id: number, field: keyof ShopifyConfig, value: string): void;
  onSelectConfig(id: number): void;
}

export default function ShopifyAccounts(props: Props) {
  const api = useApi();
  const navigate = useNavigate();

  const [selectedConfigId, setSelectedConfigId] = useState(
    props.configs.length === 1 ? props.configs[0].id : 0
  );
  const [selectedConfig, setSelectedConfig] = useState<ShopifyConfig | null>(
    props.configs.length > 0 ? props.configs[0] : null
  );

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<{
    id: number;
    storeName: string;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (props.configs.length > 0) {
      const lastConfig = props.configs[props.configs.length - 1];
      setSelectedConfigId(lastConfig.id);
      setSelectedConfig(lastConfig);
      props.onSelectConfig(lastConfig.id);
    } else {
      setSelectedConfig(null);
    }
  }, [props.configs]);

  const handleSelect = (config: ShopifyConfig) => {
    if (editingId !== config.id) {
      setSelectedConfig(config);
      setSelectedConfigId(config.id);
      props.onSelectConfig(config.id);
      setIsOpen(false);
    }
  };

  const handleEdit = (config: ShopifyConfig) => {
    setEditingId(config.id);
    setEditValue(config.storeName);
  };

  const handleSave = async (config: ShopifyConfig) => {
    try {
      await api.messaging.post(
        `Shopify/Auth/UpdateStoreNameAsync/${config.id}`,
        {
          storeName: editValue
        }
      );

      props.onChange(config.id, "storeName", editValue);
      setEditingId(null);
    } catch (error) {
      console.error("Failed to update store name", error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleDeleteClick = (id: number, storeName: string) => {
    setConfigToDelete({ id, storeName });
    setIsModalOpen(true);
  };

  const confirmDelete = async (id?: number) => {
    if (!id) return;
    await api.messaging.delete(`Shopify/Integrations/${id}`);
    props.deleteConfig(id);
    setIsModalOpen(false);
    setConfigToDelete(null);
  };

  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6">
      <table className="table-fixed w-full p-4">
        <thead>
          <tr>
            <th className="text-slate-800 text-lg font-medium leading-7 text-left pr-6">
              Accounts
            </th>
            <th className="text-slate-800 text-sm font-medium text-left pr-6"></th>
            <th className="text-right w-48">
              <PrimaryBlueButton
                label="Add Account"
                onClick={() =>
                  navigate(`/integrations/shopify/shopifycreate`, {
                    state: { teamId: props.teamId }
                  })
                }
                prefixIcon={React.createElement(IconPlus)}
                className="w-48 h-full font-medium"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {props.configs.length === 0 ? (
            <tr>
              <td colSpan={3} className="py-8 text-center">
                <div className="text-slate-500 text-sm">No Shopify accounts connected yet.</div>
                <div className="text-slate-400 text-xs mt-1">Click &ldquo;Add Account&rdquo; above to connect your first Shopify store.</div>
              </td>
            </tr>
          ) : (
          <>
          <tr>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Shopify Store Name
            </td>
            <td className="text-slate-800 text-sm font-medium text-left pr-6">
              Shopify Domain
            </td>
            <td className="w-48"></td>
          </tr>
          <tr>
            <td className="pr-6 w-full">
              {props.configs.length > 1 ? (
                <div ref={dropdownRef} className="relative w-full">
                  <button
                    className="w-full text-sm font-normal bg-white border border-gray-300 rounded-lg px-4 py-2 text-left focus:ring-1 focus:ring-[#1670e7] focus:border-[#1670e7]"
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    {props.configs.find(
                      (config) => config.id === selectedConfigId
                    )?.storeName || "Select an option"}
                  </button>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <i
                      className={`transition fa-solid ${
                        isOpen ? "fa-angle-up" : "fa-angle-down"
                      } text-slate-400 mr-1`}
                    ></i>
                  </div>
                  {isOpen && (
                    <ul className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2">
                      {props.configs.map((config) => (
                        <li
                          key={config.id}
                          className={`flex justify-between items-center rounded-lg px-3 py-3 hover:bg-slate-100 
                            text-sm font-normal hover:text-slate-800 cursor-pointer group gap-2 mb-2
                            ${
                              config.id === selectedConfigId
                                ? "text-[#3383ec] bg-slate-100"
                                : "text-slate-800"
                            }`}
                          onClick={() => handleSelect(config)}
                        >
                          {editingId === config.id ? (
                            <>
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-3 py-3 border rounded-lg"
                              />
                              <button onClick={handleCancel}>
                                <IconClose className="w-8 text-slate-500" />
                              </button>
                              <button onClick={() => handleSave(config)}>
                                <IconCheckMark className="w-8 text-slate-500" />
                              </button>
                            </>
                          ) : (
                            <span>{config.storeName}</span>
                          )}

                          {editingId !== config.id && (
                            <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(config);
                                }}
                              >
                                <IconEditPencil2 className="w-8 text-slate-800" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(config.id, config.storeName);
                                }}
                              >
                                <IconTrash2 className="w-8 text-slate-800" />
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <input
                  type="text"
                  value={selectedConfig?.storeName || ""}
                  onChange={(e) =>
                    props.onChange(
                      selectedConfigId,
                      "storeName",
                      e.target.value
                    )
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              )}
            </td>
            <td className="pr-6 w-full">
              <input
                type="text"
                value={selectedConfig?.domain || ""}
                className="w-full px-3 py-2 border rounded-lg text-slate-800 text-sm font-normal"
                disabled
              />
            </td>
            <td className="text-right w-48">
              <PrimaryGrayButton
                label="Saved"
                onClick={() => {}}
                className="w-full h-full disabled"
              />
            </td>
          </tr>
          </>
          )}
        </tbody>
      </table>

      {isModalOpen && (
        <Modal show={isModalOpen}>
          <ModalBody>
            <div className="flex items-center mb-6 justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-8 text-[#EE3763]" />
                <div className="text-slate-800 text-xl font-medium">
                  Remove Account
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)}>
                <IconClose className="w-8 text-slate-500" />
              </button>
            </div>

            <div className="text-center text-slate-800 text-sm font-normal">
              Are you sure you want to remove the account{" "}
              {configToDelete?.storeName}? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-lg px-6 py-2 bg-white border border-primary hover:border-primary-hover text-[#1670e7] text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(configToDelete?.id)}
                className="rounded-lg px-6 py-2 bg-primary border border-primary hover:bg-primary-hover hover:border-primary-hover text-white text-sm font-medium"
              >
                Remove
              </button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
